import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PRODUCTS, COWHIDE } from "@/data/site";

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-14 lg:pt-28">
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03] max-w-3xl">{title}</h1>
        {subtitle && <p className="mt-6 max-w-2xl text-lg text-teal-anchor/70 leading-relaxed font-light">{subtitle}</p>}
      </Reveal>
    </section>
  );
}

export default function Shop() {
  return (
    <div data-testid="shop-page">
      <SEO title="Shop Custom Cushion Covers" description="Explore Tanonshop's custom cushion cover options — sofas, RV dinettes, window benches, outdoor patio, throw pillows and marine cushions. Request a personalized quote." />
      <PageHero
        eyebrow="Custom options"
        title="Every cushion cover is made to order."
        subtitle="These are the projects we love most. Choose a starting point, then send your measurements — we'll tailor the fabric, closure and finish to your exact needs. No fixed prices; every quote is personal."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16">
        <Reveal>
          <div className="grid overflow-hidden rounded-sm border border-teal-anchor/12 bg-white lg:grid-cols-2">
            <div className="aspect-[4/3] overflow-hidden lg:aspect-auto">
              <img src={COWHIDE.photos[0]} alt="Cowhide print suede cushion covers" className="h-full w-full object-cover" />
            </div>
            <div className="flex flex-col justify-center p-8 lg:p-12">
              <span className="inline-flex w-fit rounded-full bg-teal-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-teal-primary">Design online · Personal quote</span>
              <h2 className="mt-5 font-serif text-3xl lg:text-4xl tracking-tight">Cowhide Print Suede Cushion Cover</h2>
              <p className="mt-4 text-teal-anchor/70 leading-relaxed">
                Our best-seller in buttery-soft cowhide-print suede. Choose your fabric, enter your dimensions,
                preview the look and add photos — then we'll personally review it and email your custom quote.
              </p>
              <Link to="/covers/cowhide" data-testid="shop-cowhide-cta" className="mt-8 inline-flex w-fit items-center gap-2 rounded-sm bg-teal-primary px-7 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-paper transition-all duration-200 hover:bg-teal-hover hover:-translate-y-px">
                Build Your Cover <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 lg:pb-32">
        <Reveal className="mb-10">
          <Eyebrow>Quote-based custom work</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl tracking-tight">Everything else, made to order</h2>
        </Reveal>
        <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <article data-testid={`product-${p.id}`} className="group">
                <div className="aspect-[4/5] overflow-hidden bg-teal-anchor/5">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-opacity duration-500 group-hover:opacity-80" />
                </div>
                <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-teal-primary">{p.tag}</p>
                <h2 className="mt-2 font-serif text-2xl tracking-tight">{p.name}</h2>
                <p className="mt-3 text-sm text-teal-anchor/70 leading-relaxed">{p.desc}</p>
                <Link to="/custom-order" data-testid={`product-quote-${p.id}`} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-teal-primary link-underline">
                  Request quote <ArrowRight size={15} />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="grain relative bg-teal-anchor">
        <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 py-24 text-center">
          <h2 className="font-serif text-3xl lg:text-5xl tracking-tight text-paper">Don't see your project?</h2>
          <p className="mx-auto mt-5 max-w-xl text-paper/75 leading-relaxed">
            If it has a cushion, we can cover it. Tell us what you have in mind and we'll craft a quote around it.
          </p>
          <Link to="/custom-order" className="mt-10 inline-flex items-center gap-2 rounded-sm bg-paper px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-teal-anchor transition-all duration-200 hover:-translate-y-px">
            Start a Custom Order <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
